import { useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { AssetImage } from "../components/AssetImage";
import { Breadcrumbs, EmptyState, Spinner } from "./ui";
import { runJob } from "../lib/makeover";
import type { LiveRender, MakeoverProgress, StartArgs } from "../lib/makeover";
import { startOptimisticSession } from "../lib/mutations";
import { conceptGroupsFromCycle as conceptGroups } from "../lib/concepts";
import { newUuid } from "../lib/util";
import type { CyclePayload, PhotoPayload, RoomPayload, SessionPayload } from "../lib/types";

type Phase = "setup" | "running" | "error";

const MAX_SOURCES = 10;

export function MakeoverView() {
  const { roomId = "" } = useParams();
  const [params] = useSearchParams();
  const refineSessionId = params.get("refineSession") || undefined;
  const refineConcept = params.get("concept") != null ? Number(params.get("concept")) : undefined;
  const navigate = useNavigate();
  const { store, rev, loading, reload, touch } = useStore();
  void rev;

  const room = store.get<RoomPayload>("ROOM", roomId);
  const photos = useMemo(() => store.photos(roomId), [store, roomId, rev]);

  const refineSession = refineSessionId ? store.get<SessionPayload>("SESSION", refineSessionId) : undefined;
  // For an iteration: build sources from a chosen concept's renders in the latest
  // cycle (the worker can GetObject those blob keys). Capped at the 10-source limit.
  const refineSources = useMemo(() => {
    if (!refineSession || refineConcept == null) return null;
    const cycles = store.cycles(refineSession.id);
    const last = cycles[cycles.length - 1];
    if (!last) return null;
    const groups = conceptGroups(last.payload as CyclePayload, store);
    const group = groups.find((g) => g.conceptIndex === refineConcept) ?? groups[0];
    if (!group) return null;
    const renders = group.renders.filter((r) => r.s3Key && r.fileName).slice(0, MAX_SOURCES);
    return {
      keys: renders.map((r) => r.s3Key!),
      fileNames: renders.map((r) => r.fileName!),
      assets: renders
        .map((r) => store.assetForLocalPath(`photos/${r.fileName}`))
        .filter((a): a is NonNullable<typeof a> => !!a),
      previousJobId: (last.payload as CyclePayload).jobId ?? undefined,
      cycleIndex: cycles.length,
      conceptName: group.name,
    };
  }, [refineSession, refineConcept, store, rev]);

  const [phase, setPhase] = useState<Phase>("setup");
  const [goal, setGoal] = useState(refineSession ? refineSession.payload.goal : "");
  const [feedback, setFeedback] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(photos.slice(-MAX_SOURCES).map((p) => p.id)),
  );
  const [progress, setProgress] = useState<MakeoverProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef({ aborted: false });

  if (loading) return <Spinner />;
  if (!room) return <EmptyState title="Room not found" icon="🔍" />;
  // Route-level guard: viewers of a shared project can't start makeovers (the
  // backend rejects viewer jobs too — this keeps deep links out of a dead end).
  if (store.roleFor(store.projectIdOf("ROOM", roomId)) === "viewer") {
    return <Navigate to={`/rooms/${roomId}`} replace />;
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SOURCES) next.add(id);
      return next;
    });
  }

  async function start() {
    // Client-generated ids; the backend finalizes the makeover onto these.
    const sessionId = refineSession ? refineSession.id : newUuid();
    const cycleId = newUuid();
    let args: StartArgs;
    if (refineSession && refineSources) {
      if (!feedback.trim()) return;
      args = {
        room: { id: roomId, name: room!.payload.name },
        goal: goal.trim() || refineSession.payload.goal,
        feedback: feedback.trim(),
        previousJobId: refineSources.previousJobId,
        sourceKeys: refineSources.keys,
        sourceFileNames: refineSources.fileNames,
        sourceAssets: refineSources.assets,
        cycleIndex: refineSources.cycleIndex,
        sessionId,
        cycleId,
      };
    } else {
      const chosen = photos.filter((p) => selected.has(p.id));
      if (chosen.length === 0 || !goal.trim()) return;
      args = {
        room: { id: roomId, name: room!.payload.name },
        goal: goal.trim(),
        sourceKeys: chosen.map((p) => p.assets[0]?.s3Key).filter(Boolean) as string[],
        sourceFileNames: chosen.map((p) => (p.payload as PhotoPayload).fileName),
        sourceAssets: chosen.map((p) => p.assets[0]).filter(Boolean),
        cycleIndex: 0,
        sessionId,
        cycleId,
      };
    }
    setPhase("running");
    setError(null);
    abortRef.current = { aborted: false };
    // Optimistically show this makeover in the room's "Past makeovers" list while
    // it generates (the AWS worker finalizes it onto the same id; reload replaces it).
    startOptimisticSession(store, roomId, sessionId, args.goal);
    touch();
    try {
      const { job } = await runJob(args, setProgress, abortRef.current);
      if (job.status === "failed" && job.results.length === 0) {
        throw new Error(job.error || "All renders failed.");
      }
      // The worker finalized the SESSION/CYCLE into sync before completing the job.
      // Pull the canonical makeover and show it (job-fallback covers any lag).
      reload();
      navigate(`/rooms/${roomId}/sessions/${sessionId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Makeover failed");
      setPhase("error");
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/" },
          { label: room.payload.name, to: `/rooms/${roomId}` },
          { label: refineSession ? "Refine" : "New makeover" },
        ]}
      />

      {phase === "setup" && (
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-ink">
            {refineSession ? "Refine these ideas" : "Start redecorating"}
          </h1>

          <div className="card p-5">
            <label className="mb-2 block text-sm font-semibold text-ink">
              What do you want? {refineSession && "(original goal)"}
            </label>
            <textarea
              className="w-full rounded-card border border-black/10 p-3 text-base"
              rows={2}
              placeholder="e.g. Cozy mid-century modern lounge with warm wood tones"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              disabled={!!refineSession}
            />
            {refineSession && (
              <>
                <label className="mb-2 mt-4 block text-sm font-semibold text-ink">What to change</label>
                <textarea
                  className="w-full rounded-card border border-black/10 p-3 text-base"
                  rows={2}
                  placeholder="e.g. Lighter walls, keep the sofa, add more plants"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </>
            )}
          </div>

          {refineSession ? (
            <div>
              <h2 className="mb-2 text-lg font-semibold text-ink">
                Building on “{refineSources?.conceptName ?? "this concept"}”
              </h2>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {(refineSources?.keys ?? []).map((k, i) => (
                  <AssetImage key={k + i} s3Key={k} alt="" className="aspect-square rounded-card shadow-card" />
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-ink">Photos to redesign</h2>
                <span className="text-sm text-muted">{selected.size} / {MAX_SOURCES} selected</span>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {photos.map((p) => {
                  const on = selected.has(p.id);
                  return (
                    <button key={p.id} onClick={() => toggle(p.id)} className="relative">
                      <AssetImage
                        s3Key={p.assets[0]?.s3Key}
                        alt=""
                        className={`aspect-square rounded-card shadow-card ring-2 transition ${on ? "ring-accent" : "ring-transparent"}`}
                      />
                      {on && (
                        <span className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-warm text-xs font-bold text-white">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="sticky bottom-4 flex justify-end">
            <button
              className="btn-warm disabled:opacity-50"
              disabled={
                refineSession
                  ? !feedback.trim() || !refineSources?.keys.length
                  : selected.size === 0 || !goal.trim()
              }
              onClick={start}
            >
              ✨ Show me some ideas
            </button>
          </div>
        </div>
      )}

      {phase === "running" && <RunningView progress={progress} />}

      {phase === "error" && (
        <EmptyState
          icon="⚠️"
          title="Makeover failed"
          subtitle={error ?? undefined}
          action={<button className="btn-warm mt-2" onClick={() => setPhase("setup")}>Try again</button>}
        />
      )}
    </div>
  );
}

const STATUS_LINES = [
  "Sketching four concepts…",
  "Mixing color palettes…",
  "Choosing furniture and materials…",
  "Styling the finishing touches…",
];

function RunningView({ progress }: { progress: MakeoverProgress | null }) {
  const total = progress?.total ?? 0;
  const done = progress?.done ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const line = STATUS_LINES[Math.min(STATUS_LINES.length - 1, Math.floor((done / Math.max(total, 1)) * STATUS_LINES.length))];

  const byConcept = new Map<number, LiveRender[]>();
  for (const r of progress?.renders ?? []) {
    const arr = byConcept.get(r.concept) ?? [];
    arr.push(r);
    byConcept.set(r.concept, arr);
  }
  const conceptName = (i: number) => progress?.concepts.find((c) => c.index === i)?.name ?? `Concept ${i + 1}`;

  return (
    <div className="space-y-5">
      <div className="card p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-semibold text-ink">{line}</span>
          <span className="text-sm text-muted">{done} / {total}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-cream">
          <div className="h-full bg-warm transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {[...byConcept.entries()].sort((a, b) => a[0] - b[0]).map(([concept, renders]) => (
        <div key={concept}>
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-warm text-xs font-semibold text-white">
              {concept + 1}
            </span>
            <h3 className="font-semibold text-ink">{conceptName(concept)}</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {renders.sort((a, b) => a.sourceIndex - b.sourceIndex).map((r) => (
              <img key={r.renderKey} src={r.url} alt="" className="aspect-square w-full rounded-card object-cover shadow-card" />
            ))}
          </div>
        </div>
      ))}

      {(progress?.renders.length ?? 0) === 0 && <Spinner label="Uploading & generating…" />}
    </div>
  );
}
